import type { Status, TicketType } from "../api/types";
import { useMe } from "../auth/session";
import { ColumnSearch } from "../ui/ColumnSearch";
import { Select, type SelectTone } from "../ui/Select";
import { useCatalogue, useGroupOptions, type TicketFilters } from "./api";
import { DONE_STATUSES, OPEN_STATUSES, PRIORITY_LABELS, STATUS_LABELS, STATUS_TONES, TYPE_LABELS } from "./labels";
import type { ColumnFilters } from "./TicketTable";

/**
 * The filters beside each column heading, shared by both ticket tables (the admin home and
 * the Tickets page). Status holds "open", "done" or "any" as well as one status.
 */
export interface FilterView {
  q: string;
  status: string;
  type: "" | TicketType;
  priority: string;
  service_id: string;
  group_id: string;
  owner: "" | "me" | "none";
  requester: "" | "me";
  raised: Since;
  updated: Since;
  due: "" | "overdue";
}

type Since = "" | "today" | "7d" | "30d";

const ALL_STATUSES: Status[] = [...OPEN_STATUSES, ...DONE_STATUSES];
// Priority colours, as on the priority badges.
const PRIORITY_DOTS: Record<number, SelectTone> = { 1: "danger", 2: "warning", 3: "neutral", 4: "neutral" };
const SINCE_OPTIONS = [
  { value: "", label: "Any time" },
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
];

export function emptyFilters(status = "open"): FilterView {
  return {
    q: "",
    status,
    type: "",
    priority: "",
    service_id: "",
    group_id: "",
    owner: "",
    requester: "",
    raised: "",
    updated: "",
    due: "",
  };
}

/** Whether anything narrows the list beyond where the table starts. */
export function isFiltered(view: FilterView, start: FilterView): boolean {
  return (Object.keys(start) as (keyof FilterView)[]).some((key) =>
    key === "q" ? view.q.trim() !== "" : view[key] !== start[key],
  );
}

function since(value: Since): string | undefined {
  if (!value) return undefined;
  const at = new Date();
  if (value === "today") at.setHours(0, 0, 0, 0);
  else at.setDate(at.getDate() - (value === "7d" ? 7 : 30));
  return at.toISOString();
}

/** The ticket-list request for these filters. */
export function filterQuery(view: FilterView, meId: string, q: string): TicketFilters {
  const statuses =
    view.status === "open"
      ? OPEN_STATUSES
      : view.status === "done"
        ? DONE_STATUSES
        : view.status === "any"
          ? ALL_STATUSES
          : [view.status];
  return {
    // "Requester: me" is everything you raised or were copied in on.
    scope: view.requester === "me" ? "mine" : "workspace",
    status: statuses,
    type: view.type ? [view.type] : undefined,
    priority: view.priority ? [Number(view.priority)] : undefined,
    service_id: view.service_id || undefined,
    group_id: view.group_id || undefined,
    owner_id: view.owner === "me" ? meId : undefined,
    no_owner: view.owner === "none" || undefined,
    overdue: view.due === "overdue" || undefined,
    created_from: since(view.raised),
    updated_from: since(view.updated),
    q,
  };
}

/** A small filter button for each column, as the table's ColumnFilters. */
export function useColumnFilters(
  view: FilterView,
  update: (patch: Partial<FilterView>) => void,
  start: FilterView,
): ColumnFilters {
  const me = useMe();
  const catalogue = useCatalogue();
  const groups = useGroupOptions();
  const mine = new Set(me.groups.map((g) => g.id));
  const myGroups = (groups.data ?? []).filter((g) => mine.has(g.id));
  const otherGroups = (groups.data ?? []).filter((g) => !mine.has(g.id));

  const menu = (
    key: keyof FilterView,
    label: string,
    groups: Parameters<typeof Select>[0]["groups"],
  ) => (
    <Select
      label={label}
      variant="icon"
      value={view[key]}
      neutralValue={start[key]}
      onChange={(value) => update({ [key]: value } as Partial<FilterView>)}
      groups={groups}
    />
  );

  return {
    ticket: (
      <ColumnSearch
        label="Search tickets"
        value={view.q}
        placeholder="Reference, title or description"
        onChange={(q) => update({ q })}
      />
    ),
    status: menu("status", "Status", [
      {
        options: [
          { value: "open", label: "Open" },
          { value: "done", label: "Resolved or closed" },
          { value: "any", label: "Any status" },
        ],
      },
      {
        label: "One status",
        options: ALL_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s], tone: STATUS_TONES[s] })),
      },
    ]),
    type: menu("type", "Type", [
      {
        options: [
          { value: "", label: "Any type" },
          { value: "incident", label: TYPE_LABELS.incident },
          { value: "service_request", label: TYPE_LABELS.service_request },
        ],
      },
    ]),
    priority: menu("priority", "Priority", [
      {
        options: [
          { value: "", label: "Any priority" },
          ...[1, 2, 3, 4].map((p) => ({ value: String(p), label: PRIORITY_LABELS[p] ?? "", tone: PRIORITY_DOTS[p] })),
        ],
      },
    ]),
    service: menu("service_id", "Service", [
      { options: [{ value: "", label: "Any service" }] },
      { options: (catalogue.data ?? []).map((svc) => ({ value: svc.id, label: svc.name })) },
    ]),
    group: menu("group_id", "Group", [
      { options: [{ value: "", label: "Any group" }] },
      ...(myGroups.length
        ? [
            { label: "My groups", options: myGroups.map((g) => ({ value: g.id, label: g.name })) },
            { label: "Other groups", options: otherGroups.map((g) => ({ value: g.id, label: g.name })) },
          ]
        : [{ options: otherGroups.map((g) => ({ value: g.id, label: g.name })) }]),
    ]),
    owner: menu("owner", "Owner", [
      {
        options: [
          { value: "", label: "Anyone" },
          { value: "me", label: "Me" },
          { value: "none", label: "Nobody yet" },
        ],
      },
    ]),
    requester: menu("requester", "Requester", [
      {
        options: [
          { value: "", label: "Anyone" },
          { value: "me", label: "Me" },
        ],
      },
    ]),
    raised: menu("raised", "Raised", [{ options: SINCE_OPTIONS }]),
    updated: menu("updated", "Updated", [{ options: SINCE_OPTIONS }]),
    due: menu("due", "Due", [
      {
        options: [
          { value: "", label: "Any time" },
          { value: "overdue", label: "Overdue", tone: "danger" },
        ],
      },
    ]),
  };
}
